/**
 * Pantalla publica de activacion: la persona elige su contrasena.
 *
 * Es la unica pantalla del sistema que se abre SIN sesion y con datos reales en
 * pantalla, asi que tiene tres reglas propias:
 *
 * 1. **Verifica el enlace ANTES de pedir nada.** Enterarse de que el enlace
 *    vencio despues de tipear la clave dos veces hace que la persona crea que
 *    se equivoco ella. Se pregunta primero y se muestra de quien es la cuenta.
 * 2. **No inventa el motivo.** El backend dice si es una invitacion o un
 *    restablecimiento y el texto cambia; no se deduce de la URL, que es lo que
 *    la persona puede pegar mal.
 * 3. **Termina ADENTRO, no en el ingreso.** El boton dice «Guardar y entrar» y
 *    eso es lo que hace: el backend devuelve el par de tokens junto con el
 *    email y la pantalla los adopta, asi que la persona pasa de elegir su
 *    contrasena a estar trabajando, sin escalas. Antes terminaba en un cartel
 *    con un boton «Ingresar» que la mandaba a tipear —treinta segundos despues
 *    de elegirla— el email y la contrasena que acababa de elegir. Es el momento
 *    de mas friccion de todo el sistema (alguien que entra por primera vez,
 *    desde un enlace que le llego por WhatsApp) y era justo donde se le pedia un
 *    tramite de mas.
 *
 *    Si el servidor no manda los tokens, la contrasena igual quedo guardada y la
 *    pantalla cae al camino de antes: el cartel con el boton al ingreso. Nunca
 *    se queda sin salida.
 * 4. **Sabe a que pantalla mandarla.** El enlace puede traer `?destino=`, y ese
 *    parametro decide donde termina. Hoy la lista esta VACIA y todos terminan en
 *    `/`: el unico destino que existia era `/portal`, y el portal del
 *    propietario se fue con la funcionalidad de expensas. El mecanismo se
 *    conserva entero porque el dia que un modulo tenga su propia pantalla de
 *    llegada es una linea, y porque lo que importa de el no es la lista sino la
 *    regla: el destino se resuelve contra una **lista fija** (`DESTINOS`) y
 *    nunca se usa el valor de la URL como ruta. Un `?destino=` que navegue a
 *    cualquier lado es una redireccion abierta, y esta es justo la pantalla que
 *    la gente abre desde un mensaje que le reenviaron.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { IconSpinner } from '@/components/shell/icons';
import { Button } from '@/components/shell/primitives';
import { api } from '@/lib/api';
import { useSessionStore } from '@/store/session';
import {
  AccesoError,
  activarConToken,
  verificarToken,
  type TokenVerificado,
} from '@/views/acceso/api';
import { Aviso, CampoContrasena, MarcoPublico } from '@/views/acceso/piezas';

type Estado =
  | { fase: 'verificando' }
  | { fase: 'listo'; datos: TokenVerificado }
  | { fase: 'rechazado'; mensaje: string; codigo: string }
  | { fase: 'activado'; email: string };

/**
 * Adonde puede mandar un `?destino=`. Lista fija a proposito: ver la regla 4.
 * Un modulo que quiera su propia pantalla de llegada agrega una entrada aca.
 *
 * Vacia desde que se fue el portal del propietario, que era su unica entrada.
 * Con la lista vacia todo `?destino=` cae a `/`, que es exactamente lo que la
 * regla 4 pide que pase con un valor que no se reconoce.
 */
const DESTINOS: Record<string, string> = {};

export function ActivarScreen() {
  const [parametros] = useSearchParams();
  const navegar = useNavigate();
  const token = parametros.get('token') ?? '';
  const destino = DESTINOS[parametros.get('destino') ?? ''] ?? '/';

  const [estado, setEstado] = useState<Estado>({ fase: 'verificando' });
  const [clave, setClave] = useState('');
  const [repeticion, setRepeticion] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vigente = true;
    if (token === '') {
      setEstado({
        fase: 'rechazado',
        mensaje:
          'El enlace no trae ningún código de acceso. Copialo entero desde el mensaje que te mandaron: los programas de mensajería a veces lo cortan.',
        codigo: 'token_ausente',
      });
      return;
    }
    void verificarToken(token)
      .then((datos) => {
        if (vigente) setEstado({ fase: 'listo', datos });
      })
      .catch((fallo: unknown) => {
        if (!vigente) return;
        setEstado({
          fase: 'rechazado',
          mensaje:
            fallo instanceof AccesoError ? fallo.message : 'No pudimos verificar el enlace.',
          codigo: fallo instanceof AccesoError ? fallo.code : '',
        });
      });
    return () => {
      vigente = false;
    };
  }, [token]);

  async function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    if (estado.fase !== 'listo') return;

    setError(null);
    if (clave !== repeticion) {
      setError('Las dos contraseñas no coinciden.');
      return;
    }

    setEnviando(true);
    try {
      const resultado = await activarConToken(token, clave, repeticion);

      // «Guardar y entrar»: si el servidor mandó la sesión, se entra. La
      // navegación va DESPUÉS de cargar la sesión para que `App` no vea un
      // estado anónimo por un instante y rebote al ingreso.
      if (typeof resultado.access === 'string' && typeof resultado.refresh === 'string') {
        api.adoptSession(resultado.access, resultado.refresh);
        await useSessionStore.getState().bootstrap();
        if (useSessionStore.getState().status === 'authenticated') {
          navegar(destino, { replace: true });
          return;
        }
        // La sesión no levantó (el servidor no contestó, por ejemplo). La
        // contraseña ya está puesta, así que se sigue por el camino de siempre
        // en vez de dejar a la persona mirando un spinner.
        api.clearSession();
      }
      setEstado({ fase: 'activado', email: resultado.email });
    } catch (fallo) {
      setError(fallo instanceof AccesoError ? fallo.message : 'No se pudo guardar la contraseña.');
      // Un token que se gastó o venció mientras la persona tipeaba deja de
      // servir: se cambia de pantalla en vez de dejarla reintentando en vano.
      if (fallo instanceof AccesoError && ['token_usado', 'token_vencido'].includes(fallo.code)) {
        setEstado({ fase: 'rechazado', mensaje: fallo.message, codigo: fallo.code });
      }
    } finally {
      setEnviando(false);
    }
  }

  // -------------------------------------------------------------------------
  // Verificando
  // -------------------------------------------------------------------------
  if (estado.fase === 'verificando') {
    return (
      <MarcoPublico>
        <div className="flex items-center gap-3 text-fg-muted" role="status" aria-live="polite">
          <IconSpinner className="h-4 w-4" />
          <span className="text-sm">Verificando el enlace…</span>
        </div>
      </MarcoPublico>
    );
  }

  // -------------------------------------------------------------------------
  // Enlace que no sirve
  // -------------------------------------------------------------------------
  if (estado.fase === 'rechazado') {
    const yaSeUso = estado.codigo === 'token_usado';
    return (
      <MarcoPublico>
        <h1 className="text-xl font-semibold tracking-tight">
          {yaSeUso ? 'Este enlace ya se usó' : 'Este enlace no sirve'}
        </h1>
        <div className="mt-4">
          <Aviso tono="error">{estado.mensaje}</Aviso>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <Button
            variant="primary"
            onClick={() => {
              navegar(destino);
            }}
          >
            Ir a la pantalla de ingreso
          </Button>
          {!yaSeUso && (
            <Button
              variant="secondary"
              onClick={() => {
                navegar('/recuperar');
              }}
            >
              Pedir un enlace nuevo
            </Button>
          )}
        </div>
        <p className="mt-6 text-xs text-fg-faint">
          Si el enlace te lo pasó quien administra el sistema, pedile que te genere uno nuevo desde
          el panel de usuarios.
        </p>
      </MarcoPublico>
    );
  }

  // -------------------------------------------------------------------------
  // Listo: ya puede entrar
  // -------------------------------------------------------------------------
  if (estado.fase === 'activado') {
    return (
      <MarcoPublico>
        <h1 className="text-xl font-semibold tracking-tight">Tu contraseña quedó lista</h1>
        <p className="mt-2 text-sm text-fg-muted">
          A partir de ahora entrás con <strong className="text-fg">{estado.email}</strong> y la
          contraseña que acabás de elegir.
        </p>
        <Button
          variant="primary"
          className="mt-6 h-11 w-full"
          onClick={() => {
            navegar(destino);
          }}
        >
          Ingresar
        </Button>
      </MarcoPublico>
    );
  }

  // -------------------------------------------------------------------------
  // El formulario
  // -------------------------------------------------------------------------
  const { datos } = estado;
  const esInvitacion = datos.purpose === 'invitation';
  const puedeEnviar = clave !== '' && repeticion !== '' && !enviando;

  return (
    <MarcoPublico>
      <h1 className="text-xl font-semibold tracking-tight">
        {esInvitacion ? 'Elegí tu contraseña' : 'Elegí una contraseña nueva'}
      </h1>
      <p className="mt-2 text-sm text-fg-muted">
        {esInvitacion ? (
          <>
            {datos.organization !== '' ? (
              <>
                <strong className="text-fg">{datos.organization}</strong> te dio acceso al sistema.{' '}
              </>
            ) : (
              'Te dieron acceso al sistema. '
            )}
            Elegí una contraseña y ya vas a poder entrar.
          </>
        ) : (
          'Elegí una contraseña nueva. Las sesiones abiertas con la anterior se van a cerrar.'
        )}
      </p>

      {/* De quién es la cuenta. Va arriba y a la vista: si alguien abre el
          enlace de otra persona, tiene que darse cuenta ANTES de tipear. */}
      <div className="mt-5 rounded-lg border border-line bg-app-subtle px-3 py-2.5">
        <p className="text-xs uppercase tracking-wide text-fg-faint">Tu cuenta</p>
        <p className="mt-0.5 truncate text-sm font-medium">{datos.email}</p>
        {datos.name !== '' && <p className="truncate text-xs text-fg-muted">{datos.name}</p>}
      </div>

      {error !== null && (
        <div className="mt-4">
          <Aviso tono="error">{error}</Aviso>
        </div>
      )}

      <form
        onSubmit={(evento) => {
          void enviar(evento);
        }}
        className="mt-5 space-y-4"
      >
        {/* El email va en un campo oculto para que el gestor de contraseñas del
            navegador sepa a qué cuenta asociar la clave que se está creando. */}
        <input type="text" name="email" autoComplete="username" value={datos.email} readOnly hidden />

        <CampoContrasena
          id="clave-nueva"
          label="Contraseña"
          valor={clave}
          onChange={setClave}
          autoComplete="new-password"
          mostrarFuerza
          email={datos.email}
          nombre={datos.name}
          autoFocus
        />
        <CampoContrasena
          id="clave-repetida"
          label="Repetila"
          valor={repeticion}
          onChange={setRepeticion}
          autoComplete="new-password"
        />

        {repeticion !== '' && clave !== repeticion && (
          <p className="text-xs text-danger">Las dos contraseñas no coinciden.</p>
        )}

        <Button type="submit" variant="primary" loading={enviando} disabled={!puedeEnviar} className="h-11 w-full">
          {enviando ? 'Guardando…' : 'Guardar y entrar'}
        </Button>
      </form>

      <p className="mt-6 text-xs text-fg-faint">
        Este enlace se puede usar una sola vez y vence el{' '}
        {new Date(datos.expires_at).toLocaleString('es-AR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })}
        .
      </p>
    </MarcoPublico>
  );
}
